import type { PublisherResultTableEntry } from '@/types/publisher-result-import';

/** The vocabulary the guided review is built on: its steps and its answers. */

export type { TrailStep } from '@/components/system';

export const CONFIRM_STEP_LABEL = 'Conferência';

/**
 * What the psychologist did to one AI-written paragraph.
 *
 * `undefined` is the third and by far the commonest state: they left it alone,
 * and it goes into the laudo as the pipeline wrote it. Only rewriting it or
 * leaving it out is worth recording.
 */
export type NarrativeDecision = { kind: 'edited'; text: string } | { kind: 'omit' };

const TRAILING_ACRONYM = /\(([^()]{2,14})\)\s*$/;
const NAME_SEPARATOR = /\s+[—–-]\s+|\s*:\s+/;

/**
 * The instrument's name cut down to what fits in a step chip.
 *
 * Publisher names arrive in two shapes — "BFP — Bateria Fatorial de
 * Personalidade" and "Bateria Fatorial de Personalidade (BFP)" — and both
 * carry the short name the psychologist actually says out loud. Anything else
 * is truncated rather than guessed at.
 */
export const instrumentShortLabel = (name: string | null | undefined) => {
    const full = name?.trim();
    if (!full) return 'Instrumento';

    const acronym = full.match(TRAILING_ACRONYM)?.[1]?.trim();
    if (acronym) return acronym;

    const short = full.split(NAME_SEPARATOR)[0]?.trim() || full;

    return short.length <= 24 ? short : `${short.slice(0, 23).trimEnd()}…`;
};

const pageList = (pages: number[]) =>
    pages.length === 1
        ? `página ${pages[0]}`
        : `páginas ${pages.slice(0, -1).join(', ')} e ${pages[pages.length - 1]}`;

/**
 * Where a classification column came from, said in one line under its table.
 *
 * A level printed beside a score reads as the psychologist's own finding once
 * the laudo is signed, so the line names the page it can be checked against.
 */
export const classificationNote = (entry: PublisherResultTableEntry) => {
    const classification = entry.table.classification;
    if (!classification || classification.source_pages.length === 0) return null;

    return `A coluna ${classification.column} vem dos pontos de corte impressos na ${pageList(
        classification.source_pages,
    )} do PDF.`;
};

export const tableLabel = (entry: PublisherResultTableEntry) =>
    entry.table.table.description?.trim() ||
    entry.table.table.title?.trim() ||
    entry.instrument.instrument_name?.trim() ||
    'Tabela importada';
